import React, { useState, useMemo, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, Cell
} from "recharts";
import PageLayout from "../components/PageLayout";
import { theme } from "../theme";
import { useSharedDataset } from "../DataContext";
import LoadingOverlay from "../components/LoadingOverlay";

const C = theme.colors.chartColors;
const NATURE_BLUE = "#6CA2AF";
const API = process.env.NODE_ENV === "development"
  ? "http://localhost:8000"
  : "https://hassaanalrifai-nexgen-demo.hf.space";

// ── UI primitives ──────────────────────────────────────────────────────────────

const Card = ({ children, style = {} }) => (
  <div style={{
    background: theme.colors.card,
    border: `1px solid ${theme.colors.cardBorder}`,
    borderRadius: 12, padding: 20, marginBottom: 20, ...style
  }}>{children}</div>
);

const ChartTitle = ({ children }) => (
  <div style={{
    fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700,
    color: theme.colors.accent, letterSpacing: 1, marginBottom: 14, textTransform: "uppercase"
  }}>{children}</div>
);

const StatTile = ({ label, value, color = theme.colors.accent, sub }) => (
  <div style={{
    background: `${color}0F`, border: `1px solid ${color}2A`,
    borderRadius: 10, padding: "16px 20px", minWidth: 130, flex: 1
  }}>
    <div style={{
      fontFamily: theme.fonts.display, fontSize: 26, fontWeight: 700,
      color
    }}>{value}</div>
    <div style={{
      fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel,
      letterSpacing: 1, marginTop: 2
    }}>{label}</div>
    {sub && <div style={{
      fontFamily: theme.fonts.mono, fontSize: 11,
      color: theme.colors.textSecondary, marginTop: 4
    }}>{sub}</div>}
  </div>
);

const SelectBox = ({ label, value, onChange, options }) => (
  <div>
    {label && (
      <div style={{
        fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel,
        letterSpacing: 1, marginBottom: 4
      }}>{label}</div>
    )}
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{
        background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
        borderRadius: 6, color: theme.colors.textPrimary,
        fontFamily: theme.fonts.body, fontSize: 13,
        padding: "8px 12px", outline: "none"
      }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Btn = ({ children, onClick, color = theme.colors.accent, variant = "primary", disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: "9px 20px",
    background: variant === "primary"
      ? `linear-gradient(135deg, ${color}, ${color}AA)`
      : `${color}18`,
    border: `1px solid ${color}44`, borderRadius: 7,
    color: variant === "primary" ? theme.colors.buttonText : color,
    fontFamily: theme.fonts.display, fontWeight: 700, fontSize: 11, letterSpacing: 1,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
    whiteSpace: "nowrap"
  }}>{children}</button>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`,
      borderRadius: 8, padding: "10px 14px",
      fontFamily: theme.fonts.mono, fontSize: 12
    }}>
      <div style={{ color: theme.colors.textSecondary, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || theme.colors.accent }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ── Severity badge ─────────────────────────────────────────────────────────────

const SeverityBadge = ({ score }) => {
  if (!score || score === 0) return null;
  const color = score >= 70 ? theme.colors.danger : score >= 40 ? theme.colors.warning : NATURE_BLUE;
  const label = score >= 70 ? "HIGH" : score >= 40 ? "MED" : "LOW";
  return (
    <span style={{
      background: `${color}22`, border: `1px solid ${color}55`,
      borderRadius: 4, padding: "2px 6px",
      fontFamily: theme.fonts.mono, fontSize: 10,
      color, fontWeight: 700, letterSpacing: 1
    }}>{label} {score}</span>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { dataset, setDataset, pageResults, setPageResult, hasData } = useSharedDataset();

  // Analysis config
  const [domain, setDomain]     = useState("network");
  const [model,    setModel]    = useState("auto");
  const [loading, setLoading]   = useState(false);
  const [multiResult, setMultiResult] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  // Smart filters (always active, no mode gating)
  const [attackTypeFilter, setAttackTypeFilter] = useState("all");
  const [severityMin, setSeverityMin]           = useState(0);
  const [showOnlyAnomalies, setShowOnlyAnomalies] = useState(false);
  const [search, setSearch]   = useState("");

  // Table
  const [sortCol, setSortCol]   = useState(null);
  const [sortAsc, setSortAsc]   = useState(true);
  const [tablePage, setTablePage] = useState(1);

  const result    = pageResults["dashboard"] || null;
  const fileName  = dataset.fileName || "";

  const availableCols = (() => {
    if (dataset.availableCols?.length) return dataset.availableCols;
    if (dataset.columns?.length)       return dataset.columns;
    if (result?.preview?.length)       return Object.keys(result.preview[0]);
    return [];
  })();

  const isReady = !!(dataset.file || hasData);

  // ── File upload ───────────────────────────────────────────────────────────────

  const handleFile = useCallback((e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.name.toLowerCase().endsWith(".csv")) {
      Papa.parse(f, {
        header: true, skipEmptyLines: true,
        complete: (results) => {
          const cols = Object.keys(results.data[0] || {});
          // Store full data in shared context so AttackAnalytics and Reports can use it
          setDataset({ file: f, fileName: f.name, availableCols: cols, data: results.data, columns: cols });
          setPageResult("dashboard", null);
          setTablePage(1);
          resetFilters();
        }
      });
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const wb    = XLSX.read(evt.target.result);
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows  = XLSX.utils.sheet_to_json(sheet);
        const cols  = Object.keys(rows[0] || {});
        // Store full data in shared context so AttackAnalytics and Reports can use it
        setDataset({ file: f, fileName: f.name, availableCols: cols, data: rows, columns: cols });
        setPageResult("dashboard", null);
        setTablePage(1);
        resetFilters();
      };
      reader.readAsArrayBuffer(f);
    }
  }, [setDataset, setPageResult]);

  const resetFilters = () => {
    setAttackTypeFilter("all");
    setSeverityMin(0);
    setShowOnlyAnomalies(false);
    setSearch("");
    setSortCol(null);
    setTablePage(1);
  };

  // ── Analyze ───────────────────────────────────────────────────────────────────

  const handleAnalyze = useCallback(async () => {
    if (!dataset.file) {
      return alert("Please use the Upload button on this page to select the file for ML analysis.");
    }
    setLoading(true);
    const form = new FormData();
    form.append("file", dataset.file);
    form.append("mode", "auto");
    form.append("domain", domain);
    form.append("columns", "");
    form.append("model", model);
    try {
      const res = await axios.post(`${API}/analyze`, form);
      if (res.data.error) {
        alert(`Analysis error: ${res.data.error}\n\nDetail: ${res.data.detail || "Check that your dataset has numeric columns."}`);
      } else {
        setPageResult("dashboard", res.data);
        setTablePage(1);
        resetFilters();
      }
    } catch (err) {
      if (err.code === "ERR_NETWORK" || err.message?.includes("Network")) {
        alert("Cannot reach backend. Make sure main.py is running:\n  uvicorn main:app --reload --port 8000");
      } else {
        alert(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [dataset.file, domain, setPageResult]);

  // ── Multi-model comparison ────────────────────────────────────────────────────

  const handleMulti = useCallback(async () => {
    if (!dataset.file) return alert("Please upload the file on this page first.");
    setLoading(true);
    const form = new FormData();
    form.append("file", dataset.file);
    form.append("domain", domain);
    form.append("columns", "");
    try {
      const res = await axios.post(`${API}/analyze_multi`, form);
      if (res.data.error) {
        alert(`Multi-model error: ${res.data.error}`);
      } else {
        setMultiResult(res.data);
        setShowComparison(true);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [dataset.file, domain]);

  // ── Export ─────────────────────────────────────────────────────────────────────

  const exportCSV = () => {
    if (!result?.preview) return;
    const headers = Object.keys(result.preview[0]);
    const csv = [
      headers.join(","),
      ...result.preview.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))
    ].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `nexgen_anomaly_report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // ── Derived data ───────────────────────────────────────────────────────────────

  const allPreview = result?.preview || [];

  // Attack field detection
  const attackField = useMemo(() => {
    if (!allPreview.length) return null;
    const cols = Object.keys(allPreview[0]);
    return cols.find(c =>
      ["attack_type","attacktype","label","class","category","type"]
        .includes(c.toLowerCase().replace(/[\s_]/g, ""))
    );
  }, [allPreview]);

  // Unique attack types for filter dropdown
  const attackTypeOptions = useMemo(() => {
    if (!attackField || !allPreview.length) return [];
    const types = [...new Set(allPreview.map(r => String(r[attackField] || "Unknown").trim()))];
    return types.sort();
  }, [allPreview, attackField]);

  // Apply smart filters — no row/anomaly limit sliders, system decides intelligently
  const tableData = useMemo(() => {
    let rows = [...allPreview];

    if (showOnlyAnomalies)            rows = rows.filter(r => r.anomaly === 1);
    if (attackTypeFilter !== "all" && attackField)
                                      rows = rows.filter(r => String(r[attackField] || "").trim() === attackTypeFilter);
    if (severityMin > 0)              rows = rows.filter(r => (r.severity || 0) >= severityMin);
    if (search.trim())                rows = rows.filter(r =>
      Object.values(r).join(" ").toLowerCase().includes(search.toLowerCase())
    );
    if (sortCol) {
      rows = [...rows].sort((a, b) => {
        const av = String(a[sortCol] ?? ""), bv = String(b[sortCol] ?? "");
        return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return rows;
  }, [allPreview, showOnlyAnomalies, attackTypeFilter, attackField, severityMin, search, sortCol, sortAsc]);

  const anomalyCount    = useMemo(() => allPreview.filter(r => r.anomaly === 1).length, [allPreview]);
  const effectiveCols   = Object.keys(tableData[0] || {}).filter(c => c !== "reason"); // keep reason hidden from main cols, show inline
  const ROWS_PER_PAGE   = 50;
  const totalPages      = Math.ceil(tableData.length / ROWS_PER_PAGE);
  const paginatedData   = tableData.slice((tablePage - 1) * ROWS_PER_PAGE, tablePage * ROWS_PER_PAGE);
  const getSortIndicator = col => sortCol !== col ? " ⇅" : sortAsc ? " ▲" : " ▼";

  // Charts
  const distributionData = useMemo(() => {
    const counts = { Normal: 0, Anomaly: 0 };
    allPreview.forEach(r => { r.anomaly === 1 ? counts.Anomaly++ : counts.Normal++; });
    return [{ name: "Normal", value: counts.Normal }, { name: "Anomaly", value: counts.Anomaly }];
  }, [allPreview]);

  const attackChartData = useMemo(() => {
    if (result?.attack_dist && Object.keys(result.attack_dist).length > 0) {
      return Object.entries(result.attack_dist)
        .map(([name, v]) => ({
          name,
          value:     typeof v === "object" ? v.total    : v,
          anomalies: typeof v === "object" ? v.anomalies : 0
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 15);
    }
    if (!attackField) return [];
    const counts = {};
    allPreview.forEach(r => {
      const v = String(r[attackField] || "Unknown").trim();
      counts[v] = (counts[v] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 15)
      .map(([name, value]) => ({ name, value, anomalies: 0 }));
  }, [result?.attack_dist, allPreview, attackField]);

  const timelineData = useMemo(() =>
    (result?.timeline || []).map((t, i) => ({ ...t, index: i })),
  [result]);

  const topAlerts = useMemo(() =>
    allPreview.filter(r => r.anomaly === 1).sort((a, b) => (b.severity || 0) - (a.severity || 0)).slice(0, 5),
  [allPreview]);

  // ── Render ─────────────────────────────────────────────────────────────────────

  return (
    <PageLayout title="Anomaly Detection" subtitle="AI-POWERED DATASET ANALYSIS · ISOLATION FOREST">
      <LoadingOverlay visible={loading} fileName={fileName} />

      {/* ── Active dataset banner ───────────────────────────────────────────── */}
      {isReady && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          padding: "10px 16px",
          background: "rgba(0,212,255,0.07)", border: "1px solid rgba(0,212,255,0.2)",
          borderRadius: 8, marginBottom: 16
        }}>
          <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.accent }}>◈ ACTIVE DATASET:</span>
          <span style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textPrimary, fontWeight: 700 }}>{fileName}</span>
          <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
            · {availableCols.length} columns
          </span>
          {!dataset.file && hasData && (
            <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.warning }}>
              ⚠ Re-upload on this page to run ML analysis
            </span>
          )}
          {dataset.file && (
            <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.success }}>
              ✓ Ready to analyze
            </span>
          )}
        </div>
      )}

      {/* ── Analysis config panel ───────────────────────────────────────────── */}
      <Card>
        {/* Row 1: domain + dataset upload + analyze */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto auto", gap: 12, alignItems: "end" }}>
          <SelectBox label="DOMAIN" value={domain} onChange={setDomain} options={[
            { value: "network",     label: "Network & Cybersecurity" },
            { value: "iot",         label: "IoT / Smart Devices" },
            { value: "industrial",  label: "Industrial / Robotics" },
            { value: "system",      label: "Systems & Cloud" },
            { value: "application", label: "Applications & Logs" },
            { value: "financial",   label: "Financial Systems" },
            { value: "healthcare",  label: "Healthcare / Medical" },
            { value: "energy",      label: "Energy & Utilities" },
            { value: "custom",      label: "Custom Dataset" },
          ]} />
          <SelectBox label="ML MODEL" value={model} onChange={setModel} options={[
            { value: "auto",                label: "Auto (RF if labeled, IF if not)" },
            { value: "random_forest",       label: "Random Forest — Production ★" },
            { value: "isolation_forest",    label: "Isolation Forest — Unsupervised" },
            { value: "logistic_regression", label: "Logistic Regression — Baseline" },
          ]} />
          <div>
            <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 4 }}>DATASET</div>
            <label style={{
              display: "block", padding: "8px 16px",
              background: isReady ? "rgba(0,230,118,0.1)" : "rgba(0,212,255,0.1)",
              border: `1px solid ${isReady ? "rgba(0,230,118,0.3)" : "rgba(0,212,255,0.3)"}`,
              borderRadius: 6, cursor: "pointer",
              fontFamily: theme.fonts.body, fontSize: 12,
              color: isReady ? theme.colors.success : theme.colors.accent,
              textAlign: "center", maxWidth: 200,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
            }}>
              {fileName || "Upload File"}
              <input hidden type="file" accept=".csv,.xlsx,.xls,.json,.parquet" onChange={handleFile} />
            </label>
          </div>
          <Btn onClick={handleAnalyze} disabled={!isReady || loading}>
            {loading ? "ANALYZING..." : "ANALYZE"}
          </Btn>
          <Btn onClick={handleMulti} disabled={!isReady || loading} color={theme.colors.warning} variant="outline">
            COMPARE MODELS
          </Btn>
        </div>

        {/* Row 2: smart filters — always active once results are available */}
        {result && (
          <div style={{
            display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap", alignItems: "flex-end",
            paddingTop: 14, borderTop: `1px solid ${theme.colors.cardBorder}`
          }}>
            {/* Attack type filter */}
            {attackTypeOptions.length > 0 && (
              <SelectBox
                label="ATTACK TYPE"
                value={attackTypeFilter}
                onChange={v => { setAttackTypeFilter(v); setTablePage(1); }}
                options={[
                  { value: "all", label: "All Types" },
                  ...attackTypeOptions.map(t => ({ value: t, label: t }))
                ]}
              />
            )}

            {/* Severity threshold */}
            <div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 4 }}>
                MIN SEVERITY: <span style={{ color: severityMin > 0 ? theme.colors.warning : theme.colors.textMuted }}>{severityMin > 0 ? severityMin : "OFF"}</span>
              </div>
              <input
                type="range" min={0} max={100} step={5} value={severityMin}
                onChange={e => { setSeverityMin(Number(e.target.value)); setTablePage(1); }}
                style={{ width: 120, accentColor: theme.colors.warning }}
              />
            </div>

            {/* Search */}
            <div>
              <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 4 }}>SEARCH</div>
              <input
                placeholder="Filter table..."
                value={search}
                onChange={e => { setSearch(e.target.value); setTablePage(1); }}
                style={{
                  background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                  borderRadius: 6, color: theme.colors.textPrimary,
                  fontFamily: theme.fonts.body, fontSize: 13,
                  padding: "8px 12px", outline: "none", width: 180
                }}
              />
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              <Btn
                onClick={() => { setShowOnlyAnomalies(!showOnlyAnomalies); setTablePage(1); }}
                variant="outline"
                color={showOnlyAnomalies ? theme.colors.danger : theme.colors.textMuted}
              >
                {showOnlyAnomalies ? "⚠ ANOMALIES ONLY" : "ANOMALIES ONLY"}
              </Btn>
              <Btn onClick={exportCSV} variant="outline">EXPORT CSV</Btn>
              {(attackTypeFilter !== "all" || severityMin > 0 || showOnlyAnomalies || search) && (
                <Btn onClick={resetFilters} variant="outline" color={theme.colors.textMuted}>CLEAR FILTERS</Btn>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* ── Stat tiles ──────────────────────────────────────────────────────── */}
      {result && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <StatTile label="TOTAL RECORDS"    value={(result.total_records ?? 0).toLocaleString()} />
          <StatTile label="ANOMALIES FOUND"  value={(result.anomalies ?? 0).toLocaleString()} color={theme.colors.danger} />
          <StatTile label="ANOMALY RATE"     value={result.anomaly_rate != null ? `${result.anomaly_rate}%` : "—"} color={theme.colors.warning} />
          <StatTile label="FILTERED ROWS"    value={tableData.length.toLocaleString()} color={NATURE_BLUE} />

          {result.features_used?.length > 0 && (
            <StatTile
              label="FEATURES USED"
              value={result.features_used.length}
              color={theme.colors.success}
              sub={result.features_used.slice(0,3).join(", ") + (result.features_used.length > 3 ? "…" : "")}
            />
          )}
          {result.metrics && (
            <StatTile label="F1 SCORE" value={`${result.metrics.f1}%`} color={theme.colors.warning}
              sub={`Acc: ${result.metrics.accuracy}% · P: ${result.metrics.precision}% · R: ${result.metrics.recall}%`} />
          )}
          {result.vendor && result.vendor !== "generic" && (
            <StatTile label="VENDOR DETECTED" value={result.vendor.replace(/_/g," ").toUpperCase()} color={NATURE_BLUE} />
          )}
        </div>
      )}

      {/* ── Model comparison panel ──────────────────────────────────────────── */}
      {showComparison && multiResult && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <ChartTitle>
              Model Comparison — {multiResult.has_labels ? "Supervised + Unsupervised" : "Unsupervised Only (no labels detected)"}
              {multiResult.recommended && (
                <span style={{ color: theme.colors.success, marginLeft: 12 }}>
                  ★ Recommended: {multiResult.recommended.replace(/_/g," ").toUpperCase()}
                </span>
              )}
            </ChartTitle>
            <button onClick={() => setShowComparison(false)} style={{
              background: "transparent", border: "none", color: theme.colors.textMuted,
              cursor: "pointer", fontSize: 18, fontFamily: theme.fonts.mono
            }}>✕</button>
          </div>
          {multiResult.vendor && multiResult.vendor !== "generic" && (
            <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: NATURE_BLUE, marginBottom: 12 }}>
              ◈ Vendor detected: {multiResult.vendor.replace(/_/g," ").toUpperCase()}
              {multiResult.attack_column && ` · Label column: ${multiResult.attack_column}`}
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: theme.fonts.mono, fontSize: 12 }}>
              <thead>
                <tr style={{ background: theme.colors.inputBg }}>
                  {["Model","Type","Anomalies","Rate %","Accuracy %","Precision %","Recall %","F1 %"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: theme.colors.textLabel,
                      borderBottom: `1px solid ${theme.colors.cardBorder}`, letterSpacing: 1, fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {multiResult.comparison.map((row, i) => {
                  const isRecommended = row.model === multiResult.recommended;
                  return (
                    <tr key={i} style={{
                      background: isRecommended ? "rgba(0,230,118,0.06)" : i % 2 === 0 ? theme.colors.rowAlt : "transparent",
                      borderBottom: `1px solid ${theme.colors.cardBorder}22`
                    }}>
                      <td style={{ padding: "9px 14px", color: isRecommended ? theme.colors.success : theme.colors.accent, fontWeight: isRecommended ? 700 : 400 }}>
                        {isRecommended ? "★ " : ""}{row.model.replace(/_/g," ").toUpperCase()}
                      </td>
                      <td style={{ padding: "9px 14px", color: row.supervised ? theme.colors.warning : theme.colors.textSecondary }}>
                        {row.supervised ? "Supervised" : "Unsupervised"}
                      </td>
                      <td style={{ padding: "9px 14px", color: theme.colors.danger }}>{row.anomalies?.toLocaleString() ?? "—"}</td>
                      <td style={{ padding: "9px 14px", color: theme.colors.textPrimary }}>{row.anomaly_rate ?? "—"}</td>
                      <td style={{ padding: "9px 14px", color: theme.colors.textPrimary }}>{row.accuracy ?? "—"}</td>
                      <td style={{ padding: "9px 14px", color: theme.colors.textPrimary }}>{row.precision ?? "—"}</td>
                      <td style={{ padding: "9px 14px", color: theme.colors.textPrimary }}>{row.recall ?? "—"}</td>
                      <td style={{ padding: "9px 14px", color: isRecommended ? theme.colors.success : theme.colors.textPrimary, fontWeight: isRecommended ? 700 : 400 }}>
                        {row.f1 ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Charts ──────────────────────────────────────────────────────────── */}
      {result && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
          <Card>
            <ChartTitle>Anomaly vs Normal Distribution</ChartTitle>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={distributionData}>
                <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 12 }} />
                <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {distributionData.map((d, i) => (
                    <Cell key={i} fill={d.name === "Anomaly" ? theme.colors.danger : theme.colors.success} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card>
            <ChartTitle>Anomaly Timeline</ChartTitle>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={timelineData}>
                <XAxis dataKey="index" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line dataKey="anomaly" stroke={theme.colors.danger} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {attackChartData.length > 0 && (
            <Card style={{ gridColumn: "1 / -1" }}>
              <ChartTitle>
                Attack Type Distribution — {result?.attack_dist
                  ? `ALL ${(result.total_records || 0).toLocaleString()} Records`
                  : `${allPreview.length.toLocaleString()} Preview Records`}
              </ChartTitle>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={attackChartData} margin={{ bottom: 60 }}>
                  <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8, padding: "10px 14px", fontFamily: theme.fonts.mono, fontSize: 12 }}>
                        <div style={{ color: theme.colors.accent, marginBottom: 6, fontWeight: 700 }}>{label}</div>
                        <div style={{ color: theme.colors.textPrimary }}>Total: <strong>{d?.value?.toLocaleString()}</strong></div>
                        {d?.anomalies > 0 && <div style={{ color: theme.colors.danger }}>Anomalies: <strong>{d?.anomalies?.toLocaleString()}</strong></div>}
                        {d?.value > 0 && (
                          <div style={{ color: theme.colors.textMuted }}>
                            % of dataset: <strong>{((d.value / (result?.total_records || 1)) * 100).toFixed(1)}%</strong>
                          </div>
                        )}
                      </div>
                    );
                  }} />
                  <Bar dataKey="value" name="Total Events" radius={[4,4,0,0]}>
                    {attackChartData.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                  </Bar>
                  {attackChartData.some(d => d.anomalies > 0) && (
                    <Bar dataKey="anomalies" name="Anomalies" fill={theme.colors.danger} radius={[4,4,0,0]} opacity={0.7} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      {/* ── Top critical alerts ──────────────────────────────────────────────── */}
      {result && topAlerts.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <ChartTitle>Top Critical Alerts — Sorted by Severity</ChartTitle>
          {topAlerts.map((row, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "8px 12px", marginBottom: 6,
              background: "rgba(255,61,87,0.08)", borderLeft: `3px solid ${theme.colors.danger}`,
              borderRadius: 4, fontFamily: theme.fonts.mono, fontSize: 12,
              color: theme.colors.textPrimary, gap: 12
            }}>
              <span>
                <span style={{ color: theme.colors.danger }}>⚠ </span>
                {attackField ? String(row[attackField] || "Unknown") : "Anomaly detected"}
                {row.reason && (
                  <span style={{ color: theme.colors.textSecondary }}> · Triggered by: {row.reason}</span>
                )}
              </span>
              <SeverityBadge score={row.severity} />
            </div>
          ))}
        </Card>
      )}

      {/* ── Data table ───────────────────────────────────────────────────────── */}
      {result && (
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {/* Table header bar */}
          <div style={{
            padding: "12px 16px", borderBottom: `1px solid ${theme.colors.cardBorder}`,
            fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary,
            display: "flex", justifyContent: "space-between", alignItems: "center"
          }}>
            <span>
              Showing <strong style={{ color: theme.colors.accent }}>{tableData.length.toLocaleString()}</strong> of{" "}
              <strong>{(result.total_records || 0).toLocaleString()}</strong> records
              {showOnlyAnomalies && <span style={{ color: theme.colors.danger }}> · Anomalies only</span>}
              {attackTypeFilter !== "all" && <span style={{ color: theme.colors.warning }}> · Type: {attackTypeFilter}</span>}
              {severityMin > 0 && <span style={{ color: theme.colors.warning }}> · Severity ≥ {severityMin}</span>}
            </span>
            <span style={{ color: theme.colors.textMuted }}>Page {tablePage} of {totalPages || 1}</span>
          </div>

          <div style={{ overflowX: "auto", maxHeight: 520, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: theme.fonts.mono, fontSize: 11 }}>
              <thead>
                <tr style={{ background: theme.colors.inputBg, position: "sticky", top: 0, zIndex: 2 }}>
                  {effectiveCols.map(col => (
                    <th key={col}
                      onClick={() => { if (sortCol === col) setSortAsc(!sortAsc); else { setSortCol(col); setSortAsc(true); } }}
                      style={{
                        padding: "10px 12px", textAlign: "left", cursor: "pointer", whiteSpace: "nowrap",
                        color: sortCol === col ? theme.colors.accent : theme.colors.textLabel,
                        borderBottom: `1px solid ${theme.colors.cardBorder}`,
                        fontFamily: theme.fonts.mono, fontSize: 11, letterSpacing: 1
                      }}>
                      {col}{getSortIndicator(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={effectiveCols.length} style={{
                      padding: "32px", textAlign: "center",
                      color: theme.colors.textMuted, fontFamily: theme.fonts.mono, fontSize: 12
                    }}>
                      No records match the current filters.
                    </td>
                  </tr>
                ) : paginatedData.map((row, i) => (
                  <tr key={i} style={{
                    background: row.anomaly === 1
                      ? `rgba(255,61,87,${Math.max((row.severity || 10) / 100, 0.07)})`
                      : i % 2 === 0 ? theme.colors.rowAlt : "transparent",
                    borderBottom: `1px solid ${theme.colors.cardBorder}22`
                  }}>
                    {effectiveCols.map((col, ci) => (
                      <td key={ci} style={{
                        padding: "7px 12px",
                        color: col === "anomaly"
                          ? row[col] === 1 ? theme.colors.danger : theme.colors.success
                          : col === "severity" && row[col] > 0
                          ? row[col] >= 70 ? theme.colors.danger : row[col] >= 40 ? theme.colors.warning : NATURE_BLUE
                          : theme.colors.textPrimary,
                        whiteSpace: "nowrap", fontSize: 12
                      }}>
                        {col === "severity" && row[col] > 0
                          ? <SeverityBadge score={row[col]} />
                          : String(row[col] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 16px", borderTop: `1px solid ${theme.colors.cardBorder}`
          }}>
            <span style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted }}>
              {tableData.length.toLocaleString()} rows · Page {tablePage}/{totalPages || 1}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              {[
                { l: "◀◀", f: () => setTablePage(1) },
                { l: "◀",  f: () => setTablePage(p => Math.max(1, p - 1)) },
                { l: "▶",  f: () => setTablePage(p => Math.min(totalPages, p + 1)) },
                { l: "▶▶", f: () => setTablePage(totalPages) }
              ].map(({ l, f }) => (
                <button key={l} onClick={f} style={{
                  padding: "6px 12px",
                  background: "rgba(0,212,255,0.08)",
                  border: `1px solid ${theme.colors.cardBorder}`,
                  borderRadius: 4, color: theme.colors.accent,
                  fontFamily: theme.fonts.mono, fontSize: 12, cursor: "pointer"
                }}>{l}</button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* ── Empty states ─────────────────────────────────────────────────────── */}
      {!isReady && (
        <Card style={{ textAlign: "center", padding: "60px 40px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⬡</div>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 18, color: theme.colors.textSecondary, letterSpacing: 2 }}>
            UPLOAD A DATASET TO BEGIN
          </div>
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginTop: 8 }}>
            Supports: CSV · XLSX · JSON · Parquet · Vendor exports (Cisco, Fortinet, AWS, Zeek, Suricata)
          </div>
        </Card>
      )}

      {isReady && !result && !loading && (
        <Card style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 14, color: theme.colors.textSecondary, letterSpacing: 1 }}>
            Dataset loaded: <span style={{ color: theme.colors.accent }}>{fileName}</span>
          </div>
          <div style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: theme.colors.textMuted, marginTop: 6 }}>
            Select a domain and click ANALYZE to run ML anomaly detection
          </div>
        </Card>
      )}

      <div style={{
        textAlign: "center", marginTop: 24, paddingBottom: 20,
        fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted
      }}>
        NexGen/Shuriken by Hassaan Alrifai · © {new Date().getFullYear()} hassaanalrifai.com · contact@hassaanalrifai.com
      </div>
    </PageLayout>
  );
}
